import React, {useEffect, useState, useRef, useCallback} from 'react';
import ModalAlert from '@/components/alerts/ModalAlert';
import i18n from 'i18n';
import {useSessionConfig, useUrls} from 'hooks/useConfig';
import SessionManager, {SessionEvent, isSessionDebugMode} from '@/services/SessionManager';

// AppLogout module loaded
if (isSessionDebugMode()) {
    console.log('🔄 [AppLogout] Module loaded at', new Date().toISOString());
}

export const AppLogout = ({children}: any) => {
    if (isSessionDebugMode()) {
        console.log('🚀 [AppLogout] Component rendering...');
    }
    const {sessionTime, warningTime, isLoaded: configLoaded} = useSessionConfig();
    const {urls} = useUrls();
    
    const sessionManager = useRef<SessionManager | null>(null);

    const [isSessionTimeout, setIsSessionTimeout] = useState(false);
    const [remainingTime, setRemainingTime] = useState(warningTime);
    const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
    const [isExtendingSession, setIsExtendingSession] = useState(false);

    const handleLogout = useCallback(() => {
        if (sessionManager.current) {
            localStorage.setItem('forceLogout', Date.now().toString());
            window.location.href = (window as any).URLS?.logOutView || '/auth/logout';
        }
    }, []);

    const handleWarningStarted = useCallback((event: SessionEvent) => {
        if (event.data?.remainingTime !== undefined) {
            setRemainingTime(event.data.remainingTime);
            if (event.data.remainingTime <= 0) {
                handleLogout();
                return;
            }
        }
        setIsSessionTimeout(true);
    }, [handleLogout]);

    const handleWarningEnded = useCallback(() => {
        setIsSessionTimeout(false);
    }, []);

    const handleForceLogout = useCallback(() => {
        setShowForceLogoutModal(true);
    }, []);


    const handleSessionExtended = useCallback((event: SessionEvent) => {
        if (isSessionDebugMode()) {
            console.log('[AppLogout] Session extended successfully - re-enabling button', event);
        }
        setIsSessionTimeout(false);
        setIsExtendingSession(false);
    }, []);

    // Set up SessionManager only once
    useEffect(() => {
        if (!sessionManager.current) {
            sessionManager.current = SessionManager.getInstance();
            if (isSessionDebugMode()) {
                console.log('[AppLogout] SessionManager instance created');
            }
        }
    }, []);

    // Set up event listeners only once
    useEffect(() => {
        if (!sessionManager.current) {return;}

        if (isSessionDebugMode()) {
            console.log('[AppLogout] Setting up event listeners', {
                hasWarningStarted: !!handleWarningStarted,
                hasWarningEnded: !!handleWarningEnded,
                hasForceLogout: !!handleForceLogout,
                hasSessionExtended: !!handleSessionExtended
            });
        }
        
        sessionManager.current.addEventListener('warning-started', handleWarningStarted);
        sessionManager.current.addEventListener('warning-ended', handleWarningEnded);
        sessionManager.current.addEventListener('force-logout', handleForceLogout);
        sessionManager.current.addEventListener('session-extended', handleSessionExtended);
        if (isSessionDebugMode()) {
            console.log('[AppLogout] Event listeners set up successfully');
        }

        return () => {
            if (sessionManager.current) {
                if (isSessionDebugMode()) {
                    console.log('[AppLogout] Cleaning up event listeners');
                }
                sessionManager.current.removeEventListener('warning-started', handleWarningStarted);
                sessionManager.current.removeEventListener('warning-ended', handleWarningEnded);
                sessionManager.current.removeEventListener('force-logout', handleForceLogout);
                sessionManager.current.removeEventListener('session-extended', handleSessionExtended);
            }
        };
    }, [handleWarningStarted, handleWarningEnded, handleForceLogout, handleSessionExtended]);

    // Initialize configuration when it changes
    useEffect(() => {
        if (!configLoaded || !sessionManager.current) {return;}

        if (isSessionDebugMode()) {
            console.log('[AppLogout] Updating SessionManager configuration:', {
                sessionTime,
                warningTime,
                configLoaded,
                isLoggedIn: localStorage.getItem('isLoggedIn'),
                debugMode: isSessionDebugMode()
            });
        }

        sessionManager.current.initialize({
            sessionTime: sessionTime,
            warningTime: warningTime
        });
    }, [configLoaded, sessionTime, warningTime]);

    const extendSession = async () => {
        if (!sessionManager.current || isExtendingSession) {return;}

        if (isSessionDebugMode()) {
            console.log('[AppLogout] Starting session extension...');
        }
        setIsExtendingSession(true);
        
        try {
            const success = await sessionManager.current.extendSession();
            if (isSessionDebugMode()) {
                console.log('[AppLogout] Session extension result:', success);
            }
            if (!success) {
                console.error('Session extension failed');
                setIsExtendingSession(false);
            }
            // Note: On success, the button should be re-enabled by the 'session-extended' event
        } catch (error) {
            console.error('Error extending session:', error);
            setIsExtendingSession(false);
        }
    };


    return (
        <>
            {children}

            <ModalAlert
                isOpen={showForceLogoutModal}
                onClose={() => setShowForceLogoutModal(false)}
                variant="warning"
                title={i18n.t('jt_session_expired.text')}
                message={i18n.t('session_expiry_tab.text')}
                dismissible={false}
                backdrop="static"
                actions={[
                    {
                        label: i18n.t('session_logout.text'),
                        variant: 'danger',
                        onClick: handleLogout
                    }
                ]}
            />

            <ModalAlert
                isOpen={isSessionTimeout}
                onClose={() => {}}
                variant="warning"
                title={i18n.t('session_timeout_warning.text')}
                message={`${i18n.t('session_expiry.text')} ${i18n.t('session_time_rem.text')} ${remainingTime} ${i18n.t('session_seconds.text')}`}
                dismissible={false}
                backdrop="static"
                actions={[
                    {
                        label: i18n.t('session_logout.text'),
                        variant: 'danger',
                        onClick: handleLogout
                    },
                    {
                        label: i18n.t('session_stay_log.text'),
                        variant: 'primary',
                        onClick: extendSession,
                        loading: isExtendingSession
                    }
                ]}
            />
        </>
    );
};